import { useEffect, useState, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getDateLocale } from '../i18n';
import { TrendingUp, Bot, CalendarDays, DollarSign, Zap, BarChart2 } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────

interface Summary {
  revenue_this_month: number;
  bookings_this_month: number;
  ai_bookings_12m: number;
  widget_bookings_12m: number;
  total_bookings_12m: number;
  currency: string;
}

interface WeeklyOcc { week: string; occupancy_percent: number; bookings: number }
interface RevenueMonth { month: string; revenue: number; bookings: number }
interface SourceMonth { month: string; ai: number; widget: number; admin: number; other: number }
interface DowPoint { day: string; arrivals: number }
interface DurationPoint { month: string; avg_nights: number }
interface TopBerth { berth_id: number; label: string; bookings: number }
interface TopQuestion { question: string; count: number }

interface AnalyticsData {
  summary: Summary;
  weekly_occupancy: WeeklyOcc[];
  revenue_by_month: RevenueMonth[];
  bookings_by_source: SourceMonth[];
  dow_heatmap: DowPoint[];
  avg_stay_duration: DurationPoint[];
  top_berths: TopBerth[];
  top_questions: TopQuestion[];
}

// ── Helpers ──────────────────────────────────────────────────────

/** Backend sends Sun…Sat — map to localized short weekday labels. */
const BACKEND_DOW_ORDER = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

function localizeDowLabel(backendDay: string, locale: string): string {
  const idx = BACKEND_DOW_ORDER.indexOf(backendDay as (typeof BACKEND_DOW_ORDER)[number]);
  if (idx < 0) return backendDay;
  const anchor = new Date(2024, 5, 9 + idx);
  return anchor.toLocaleDateString(locale, { weekday: 'short' });
}

function fmtMonthWithLocale(m: unknown, locale: string) {
  if (typeof m !== 'string') return String(m ?? '');
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString(locale, { month: 'short', year: '2-digit' });
}

// ── Sub-components ───────────────────────────────────────────────

function StatCard({
  label, value, sub, icon: Icon, color = 'text-brand-600',
}: {
  label: string; value: string | number; sub?: string;
  icon: React.ComponentType<{ className?: string }>; color?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
      <div className={`p-2.5 rounded-lg bg-gray-50 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-gray-500 truncate">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
      {children}
    </div>
  );
}

const TOOLTIP_STYLE = { fontSize: '12px', borderRadius: '6px' };
const AXIS_TICK = { fontSize: 11, fill: '#9ca3af' };

// ── Main component ───────────────────────────────────────────────

export function AnalyticsPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const { profile, activeMarinaId } = useAuth();
  const toast = useToast();

  const dateLocale = getDateLocale();
  const fmtMonth = useCallback(
    (m: unknown) => fmtMonthWithLocale(m, dateLocale),
    [dateLocale],
  );

  const fmtOccupancyTooltip = useCallback(
    (value: unknown) => [`${Number(value ?? 0)}%`, t('analytics.tooltipOccupancySeries')] as const,
    [t],
  );

  const fmtCurrencyTooltip = useCallback(
    (value: unknown) => [`€${Number(value ?? 0).toLocaleString()}`, t('analytics.tooltipRevenueSeries')] as const,
    [t],
  );

  const fmtNightsTooltip = useCallback(
    (value: unknown) => {
      const n = Number(value ?? 0);
      return [t('analytics.nights', { count: n }), t('analytics.tooltipAvgStaySeries')] as [string, string];
    },
    [t],
  );

  useEffect(() => {
    setLoading(true);
    api.getAnalyticsDetailed()
      .then((d) => setData(d as unknown as AnalyticsData))
      .catch(() => toast.error(t('analytics.failedToLoad')))
      .finally(() => setLoading(false));
  }, [activeMarinaId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-gray-400 gap-3">
        <BarChart2 className="w-5 h-5 animate-pulse" />
        {t('common.loading')}
      </div>
    );
  }

  if (!data) return null;

  const { summary } = data;

  // ── Insight callout ──────────────────────────────────────────

  const TIER_PRICE: Record<string, number> = { starter: 79, growth: 199, group: 499, enterprise: 599 };
  const tier = profile?.role === 'platform_superadmin' ? null : null; // tier comes from billing, not profile
  const monthlyPlan = tier ? (TIER_PRICE[tier] ?? 0) : null;
  const lastMonthAiBookings = data.bookings_by_source
    .slice(-2, -1)[0]; // second-to-last month = last full month
  const lastMonthAi = lastMonthAiBookings?.ai ?? 0;
  const costPerBooking = monthlyPlan && lastMonthAi > 0
    ? (monthlyPlan / lastMonthAi).toFixed(2)
    : null;

  const aiSharePct =
    summary.total_bookings_12m > 0
      ? Math.round((summary.ai_bookings_12m / summary.total_bookings_12m) * 100)
      : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('analytics.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('analytics.subtitle')}</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={DollarSign}
          label={t('analytics.revenueThisMonth', { currency: summary.currency })}
          value={`€${summary.revenue_this_month.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          color="text-emerald-600"
        />
        <StatCard
          icon={CalendarDays}
          label={t('analytics.bookingsThisMonth')}
          value={summary.bookings_this_month}
          sub={t('analytics.totalBookings12mHint', { count: summary.total_bookings_12m })}
          color="text-brand-600"
        />
        <StatCard
          icon={Bot}
          label={t('analytics.aiAssistedBookings12m')}
          value={summary.ai_bookings_12m}
          sub={summary.total_bookings_12m > 0 ? t('analytics.pctOfAllBookings', {
            pct: Math.round((summary.ai_bookings_12m / summary.total_bookings_12m) * 100),
          }) : undefined}
          color="text-violet-600"
        />
        <StatCard
          icon={TrendingUp}
          label={t('analytics.widgetFormBookings12m')}
          value={summary.widget_bookings_12m}
          color="text-amber-600"
        />
      </div>

      {/* AI insight callout */}
      {lastMonthAi > 0 && (
        <div className="bg-gradient-to-r from-violet-50 to-brand-50 border border-violet-200 rounded-xl p-4 flex items-start gap-3">
          <Zap className="w-5 h-5 text-violet-600 mt-0.5 shrink-0" />
          <p className="text-sm text-gray-700">
            <strong className="text-violet-700">
              {t('analytics.aiInsightHandled', { count: lastMonthAi })}
            </strong>
            {' '}
            {summary.total_bookings_12m > 0
              ? t('analytics.aiInsightTailWithPct', { pct: aiSharePct })
              : t('analytics.aiInsightTailNoPct')}
            {costPerBooking && (
              <>
                {' '}
                {t('analytics.aiInsightCost', { cost: costPerBooking })}
              </>
            )}
          </p>
        </div>
      )}

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title={t('analytics.chartWeeklyOccupancy')}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.weekly_occupancy} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="week" tick={AXIS_TICK} />
              <YAxis tick={AXIS_TICK} unit="%" domain={[0, 100]} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={fmtOccupancyTooltip} />
              <Line type="monotone" dataKey="occupancy_percent" stroke="#2563eb" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t('analytics.chartRevenueByMonth')}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.revenue_by_month} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={AXIS_TICK} tickFormatter={fmtMonth} />
              <YAxis tick={AXIS_TICK} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={fmtMonth} formatter={fmtCurrencyTooltip} />
              <Bar dataKey="revenue" fill="#10b981" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title={t('analytics.chartBookingsBySourceMonthly')}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.bookings_by_source} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={AXIS_TICK} tickFormatter={fmtMonth} />
              <YAxis tick={AXIS_TICK} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={fmtMonth} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} formatter={(v: string) => v} />
              <Bar dataKey="ai" stackId="a" fill="#7c3aed" name={t('analytics.legendAiChat')} radius={[0, 0, 0, 0]} />
              <Bar dataKey="widget" stackId="a" fill="#2563eb" name={t('analytics.legendWidgetForm')} />
              <Bar dataKey="admin" stackId="a" fill="#94a3b8" name={t('analytics.legendAdmin')} />
              <Bar dataKey="other" stackId="a" fill="#e2e8f0" name={t('analytics.legendOther')} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t('analytics.chartAvgStayDuration')}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.avg_stay_duration} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={AXIS_TICK} tickFormatter={fmtMonth} />
              <YAxis tick={AXIS_TICK} unit={t('analytics.nightsUnit')} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={fmtMonth} formatter={fmtNightsTooltip} />
              <Line type="monotone" dataKey="avg_nights" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Heatmap + Tables row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Day-of-week arrivals */}
        <ChartCard title={t('analytics.chartArrivalsByDow')}>
          <div className="space-y-2 mt-1">
            {data.dow_heatmap.map(({ day, arrivals }) => {
              const max = Math.max(...data.dow_heatmap.map((d) => d.arrivals), 1);
              const pct = Math.round((arrivals / max) * 100);
              return (
                <div key={day} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-gray-500 w-8">{localizeDowLabel(day, dateLocale)}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-3">
                    <div
                      className="h-3 rounded-full bg-brand-500 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-6 text-right">{arrivals}</span>
                </div>
              );
            })}
          </div>
        </ChartCard>

        {/* Most used berths */}
        <ChartCard title={t('analytics.chartMostBookedBerths')}>
          {data.top_berths.length === 0 ? (
            <p className="text-sm text-gray-400">{t('analytics.noDataYet')}</p>
          ) : (
            <div className="space-y-2">
              {data.top_berths.map(({ berth_id, label, bookings: cnt }) => {
                const max = data.top_berths[0]?.bookings ?? 1;
                const pct = Math.round((cnt / max) * 100);
                return (
                  <div key={berth_id} className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700 w-12 truncate">{label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-gray-500 w-6 text-right">{cnt}</span>
                  </div>
                );
              })}
            </div>
          )}
        </ChartCard>

        {/* Top questions */}
        <ChartCard title={t('analytics.chartTopQuestions')}>
          {data.top_questions.length === 0 ? (
            <p className="text-sm text-gray-400">{t('analytics.noConversationDataYet')}</p>
          ) : (
            <ol className="space-y-2">
              {data.top_questions.map(({ question, count }, i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span className="shrink-0 text-gray-400 font-medium w-4">{i + 1}.</span>
                  <span className="text-gray-700 flex-1 line-clamp-2 capitalize">{question}</span>
                  <span className="shrink-0 text-gray-400 font-medium">{count}×</span>
                </li>
              ))}
            </ol>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
